/**
 * ExclusivesSection - Retailer Exclusives with Products
 *
 * Displays participating products with "Shop Online" functionality
 * and retailer exclusive offers. Combines the product carousel with
 * retailer-specific promotions.
 */

import React, { useState, useCallback, useEffect } from "react";
import {
    FaChevronLeft,
    FaChevronRight,
    FaShoppingCart,
    FaExternalLinkAlt,
    FaTimes,
    FaMapMarkerAlt,
    FaGift,
} from "react-icons/fa";
import {ExternalLink} from "@/widgets/ExternalLink";
import "./ExclusivesSection.scss";

interface Product {
    id: string;
    name: string;
    imageUrl: string;
}

interface Retailer {
    id: string;
    name: string;
    logoUrl?: string;
    productUrl: string;
    icon?: React.ReactNode;
    type: "online" | "in-store" | "both";
    exclusive?: string;
}

// Default Monster Energy products
const DEFAULT_PRODUCTS: Product[] = [
    {
        id: "monster-zero-sugar",
        name: "Monster Energy Zero Sugar",
        imageUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/monster-energy-zero-sugar.webp",
    },
    {
        id: "monster-original",
        name: "Monster Energy Original",
        imageUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/monster-energy-original-green.webp",
    },
    {
        id: "monster-zero-ultra",
        name: "Monster Energy Zero Ultra",
        imageUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/monster-energy-zero-ultra.webp",
    },
];

const DEFAULT_RETAILERS: Retailer[] = [
    {
        id: "7-eleven",
        name: "7-Eleven",
        productUrl: "https://www.7-eleven.com",
        logoUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/7-11-icon7.png",
        type: "in-store",
        exclusive: "500 Beast Points + Exclusive 7-Eleven Camo",
    },
    {
        id: "quiktrip",
        name: "QuikTrip",
        productUrl: "https://www.quiktrip.com",
        logoUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/quiktrip-icon.webp",
        type: "in-store",
        exclusive: "1,000 Beast Points + Bonus Loot Box",
    },
    {
        id: "amazon",
        name: "Amazon",
        productUrl: "https://amazon.com/s?k=monster+energy",
        logoUrl: "https://storage.googleapis.com/cdn_devourgo/images/monster-energy/amazon-icon.webp",
        type: "online",
        exclusive: "750 Beast Points + Digital Collectible",
    },
];

interface ExclusivesSectionProps {
    products?: Product[];
    retailers?: Retailer[];
}

export const ExclusivesSection = ({
    products = DEFAULT_PRODUCTS,
    retailers = DEFAULT_RETAILERS,
}: ExclusivesSectionProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [activeTab, setActiveTab] = useState<"online" | "in-store">("online");

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape" && isDrawerOpen) {
            setIsDrawerOpen(false);
            setSelectedProduct(null);
        }
    }, [isDrawerOpen]);

    useEffect(() => {
        if (isDrawerOpen) {
            document.addEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.body.style.overflow = "";
        };
    }, [isDrawerOpen, handleKeyDown]);

    const handlePrev = () => {
        setCurrentIndex((prev) => prev === 0 ? products.length - 1 : prev - 1);
    };

    const handleNext = () => {
        setCurrentIndex((prev) => prev === products.length - 1 ? 0 : prev + 1);
    };

    const handleShopOnline = (product: Product) => {
        setSelectedProduct(product);
        setIsDrawerOpen(true);
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setIsDrawerOpen(false);
            setSelectedProduct(null);
        }
    };

    const onlineRetailers = retailers.filter((r) => r.type === "online" || r.type === "both");
    const inStoreRetailers = retailers.filter((r) => r.type === "in-store" || r.type === "both");
    const currentProduct = products[currentIndex];

    return (
        <section className="exclusives-section">
            {/* Hero Banner */}
            <div className="exclusives-section__hero">
                <div className="exclusives-section__hero-content">
                    <h1 className="exclusives-section__hero-title">
                        RETAILER EXCLUSIVES
                    </h1>
                    <p className="exclusives-section__hero-subtitle">
                        Shop at participating retailers to unlock exclusive in-game rewards
                    </p>
                </div>
            </div>

            {/* Retailer Cards */}
            <div className="exclusives-section__retailers">
                <h2 className="exclusives-section__section-title">
                    EXCLUSIVE OFFERS
                </h2>
                <div className="exclusives-section__retailer-grid">
                    {retailers.map((retailer) =>
                        <ExternalLink
                            key={retailer.id}
                            className="exclusives-section__retailer-card"
                            href={retailer.productUrl}
                        >
                            <div className="exclusives-section__retailer-icon">
                                {retailer.logoUrl
                                    ? <img src={retailer.logoUrl} alt={retailer.name} />
                                    : retailer.icon || <FaShoppingCart size={24} />
                                }
                            </div>
                            <div className="exclusives-section__retailer-details">
                                <h3>{retailer.name}</h3>
                                {retailer.exclusive &&
                                    <div className="exclusives-section__retailer-exclusive">
                                        <FaGift size={12} />
                                        <span>{retailer.exclusive}</span>
                                    </div>
                                }
                            </div>
                            <FaExternalLinkAlt size={14} className="exclusives-section__retailer-arrow" />
                        </ExternalLink>)}
                </div>
            </div>

            {/* Products Carousel */}
            <div className="exclusives-section__products">
                <h2 className="exclusives-section__section-title">
                    PARTICIPATING PRODUCTS
                </h2>

                <div className="exclusives-section__carousel">
                    <button
                        className="exclusives-section__nav"
                        onClick={handlePrev}
                        aria-label="Previous product"
                    >
                        <FaChevronLeft size={18} />
                    </button>

                    <div className="exclusives-section__card">
                        <img
                            src={currentProduct.imageUrl}
                            alt={currentProduct.name}
                            className="exclusives-section__product-image"
                        />
                        <h3 className="exclusives-section__product-name">
                            {currentProduct.name}
                        </h3>
                        <button
                            className="exclusives-section__shop-btn"
                            onClick={() => handleShopOnline(currentProduct)}
                        >
                            <span>SHOP ONLINE</span>
                            <FaChevronRight size={12} />
                        </button>
                    </div>

                    <button
                        className="exclusives-section__nav"
                        onClick={handleNext}
                        aria-label="Next product"
                    >
                        <FaChevronRight size={18} />
                    </button>
                </div>

                <div className="exclusives-section__dots">
                    {products.map((_, idx) =>
                        <button
                            key={idx}
                            className={`exclusives-section__dot ${idx === currentIndex ? "exclusives-section__dot--active" : ""}`}
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Go to product ${idx + 1}`}
                        />)}
                </div>
            </div>

            {/* Retailer Drawer Modal */}
            {isDrawerOpen && selectedProduct &&
                <div
                    className="exclusives-section__drawer-overlay"
                    onClick={handleOverlayClick}
                >
                    <div className="exclusives-section__drawer">
                        <div className="exclusives-section__drawer-header">
                            <div className="exclusives-section__drawer-product">
                                <img
                                    src={selectedProduct.imageUrl}
                                    alt={selectedProduct.name}
                                    className="exclusives-section__drawer-product-img"
                                />
                                <div>
                                    <h3 className="exclusives-section__drawer-product-name">
                                        {selectedProduct.name}
                                    </h3>
                                    <p className="exclusives-section__drawer-subtitle">
                                        Select a retailer to purchase
                                    </p>
                                </div>
                            </div>
                            <button
                                className="exclusives-section__drawer-close"
                                onClick={() => {
                                    setIsDrawerOpen(false);
                                    setSelectedProduct(null);
                                }}
                                aria-label="Close"
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>

                        <div className="exclusives-section__drawer-tabs">
                            <button
                                className={`exclusives-section__drawer-tab ${activeTab === "online" ? "exclusives-section__drawer-tab--active" : ""}`}
                                onClick={() => setActiveTab("online")}
                            >
                                <FaShoppingCart size={14} />
                                Shop Online
                            </button>
                            <button
                                className={`exclusives-section__drawer-tab ${activeTab === "in-store" ? "exclusives-section__drawer-tab--active" : ""}`}
                                onClick={() => setActiveTab("in-store")}
                            >
                                <FaMapMarkerAlt size={14} />
                                Find In Store
                            </button>
                        </div>

                        <div className="exclusives-section__drawer-content">
                            {activeTab === "online" &&
                                <div className="exclusives-section__retailer-list">
                                    {onlineRetailers.map((retailer) =>
                                        <ExternalLink
                                            key={retailer.id}
                                            className="exclusives-section__retailer-btn"
                                            href={retailer.productUrl}
                                        >
                                            <div className="exclusives-section__retailer-info">
                                                <div className="exclusives-section__retailer-btn-icon">
                                                    {retailer.logoUrl
                                                        ? <img src={retailer.logoUrl} alt={retailer.name} />
                                                        : retailer.icon || <FaShoppingCart size={18} />
                                                    }
                                                </div>
                                                <div className="exclusives-section__retailer-btn-details">
                                                    <span className="exclusives-section__retailer-name">
                                                        {retailer.name}
                                                    </span>
                                                    {retailer.exclusive &&
                                                        <span className="exclusives-section__retailer-bonus">
                                                            <FaGift size={10} /> {retailer.exclusive}
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                            <FaExternalLinkAlt size={12} className="exclusives-section__retailer-arrow" />
                                        </ExternalLink>)}
                                </div>
                            }

                            {activeTab === "in-store" &&
                                <div className="exclusives-section__retailer-list">
                                    {inStoreRetailers.map((retailer) =>
                                        <ExternalLink
                                            key={retailer.id}
                                            className="exclusives-section__retailer-btn"
                                            href={retailer.productUrl}
                                        >
                                            <div className="exclusives-section__retailer-info">
                                                <div className="exclusives-section__retailer-btn-icon">
                                                    {retailer.logoUrl
                                                        ? <img src={retailer.logoUrl} alt={retailer.name} />
                                                        : retailer.icon || <FaMapMarkerAlt size={18} />
                                                    }
                                                </div>
                                                <div className="exclusives-section__retailer-btn-details">
                                                    <span className="exclusives-section__retailer-name">
                                                        {retailer.name}
                                                    </span>
                                                    {retailer.exclusive &&
                                                        <span className="exclusives-section__retailer-bonus">
                                                            <FaGift size={10} /> {retailer.exclusive}
                                                        </span>
                                                    }
                                                </div>
                                            </div>
                                            <FaExternalLinkAlt size={12} className="exclusives-section__retailer-arrow" />
                                        </ExternalLink>)}
                                </div>
                            }
                        </div>

                        <div className="exclusives-section__drawer-footer">
                            <p>Verify your purchase to earn bonus XP!</p>
                        </div>
                    </div>
                </div>
            }
        </section>
    );
};
